import { useMemo, useState } from "react";
import { post, refresh, useApi } from "../lib/api.ts";
import type { DiffFile, Project, Session, SystemStatus, WorktreeDiff } from "../lib/types.ts";
import { ErrorBox, Field, Loading, Modal, useAction, useToast } from "./ui.tsx";

const STATUS_MARK: Record<DiffFile["status"], string> = { modified: "M", added: "A", deleted: "D", renamed: "R", untracked: "U" };

/** `repo` is a workspace repository folder; "" is the session folder itself. */
export function useDiff(sessionId: string, repo = "") {
  return useApi<WorktreeDiff>(`/api/sessions/${sessionId}/diff${repo ? `?repo=${encodeURIComponent(repo)}` : ""}`, { revalidateOnFocus: false });
}

/** The repositories a session spans: the session folder ("") and, in a workspace, each repository. */
export function sessionRepos(project?: Project): string[] {
  return project?.kind === "workspace" ? ["", ...project.repos] : [""];
}

export const repoLabel = (repo: string) => (repo ? `${repo}/` : "workspace root");

export function DiffFiles({ diff, onPick }: { diff: WorktreeDiff; onPick?: (path: string) => void }) {
  if (!diff.files.length) return <div className="muted small">No changes yet.</div>;
  return (
    <div className="stack tight">
      {diff.files.map((f) => (
        <div key={f.path} className="row nowrap between small">
          <span className="row nowrap grow">
            <span className="pill mono" title={f.status}>
              {STATUS_MARK[f.status]}
            </span>
            {onPick ? (
              <a href={`#file-${encodeURIComponent(f.path)}`} className="ellipsis mono" onClick={() => onPick(f.path)}>
                {f.path}
              </a>
            ) : (
              <span className="ellipsis mono">{f.path}</span>
            )}
          </span>
          <span className="nowrap">
            <span className="plus">+{f.additions}</span> <span className="minus">−{f.deletions}</span>
          </span>
        </div>
      ))}
    </div>
  );
}

function Patch({ patch }: { patch: string }) {
  const lines = useMemo(() => patch.split("\n"), [patch]);
  if (!patch.trim()) return <div className="muted small">No textual changes to tracked files. New untracked files are listed above.</div>;
  return (
    <div className="diff">
      {lines.map((l, i) => {
        let cls = "";
        let id: string | undefined;
        if (l.startsWith("diff --git")) {
          cls = "file";
          const m = l.match(/ b\/(.+)$/);
          id = m ? `file-${encodeURIComponent(m[1]!)}` : undefined;
        } else if (l.startsWith("@@")) cls = "hunk";
        else if (l.startsWith("+") && !l.startsWith("+++")) cls = "add";
        else if (l.startsWith("-") && !l.startsWith("---")) cls = "del";
        return (
          <div key={i} id={id} className={cls}>
            {l || " "}
          </div>
        );
      })}
    </div>
  );
}

export function SessionDiff({ session, project, lastSummary }: { session: Session; project?: Project; lastSummary?: string }) {
  const repos = sessionRepos(project);
  // Start on the first repository rather than the workspace root, which rarely changes.
  const [picked, setPicked] = useState<string | null>(null);
  const repo = picked ?? (repos.length > 1 ? repos[1]! : "");
  return (
    <div className="stack">
      {repos.length > 1 && (
        <div className="row">
          <div className="seg" role="tablist">
            {repos.map((r) => (
              <RepoTab key={r} session={session} repo={r} on={r === repo} onClick={() => setPicked(r)} />
            ))}
          </div>
          <span className="muted small">Each repository is committed and pushed on its own.</span>
        </div>
      )}
      <RepoDiff key={repo} session={session} repo={repo} lastSummary={lastSummary} />
    </div>
  );
}

function RepoTab({ session, repo, on, onClick }: { session: Session; repo: string; on: boolean; onClick: () => void }) {
  const diff = useDiff(session.id, repo);
  const n = diff.data?.files.length ?? 0;
  return (
    <button type="button" role="tab" aria-selected={on} className={on ? "on" : ""} onClick={onClick}>
      <span className="mono">{repoLabel(repo)}</span>
      {n > 0 && <span className="muted"> · {n}</span>}
    </button>
  );
}

function RepoDiff({ session, repo, lastSummary }: { session: Session; repo: string; lastSummary?: string }) {
  const diff = useDiff(session.id, repo);
  const system = useApi<SystemStatus>("/api/system");
  const toast = useToast();
  const [committing, setCommitting] = useState(false);
  const [prOpen, setPrOpen] = useState(false);
  const [push, pushing] = useAction(async () => {
    await post(`/api/sessions/${session.id}/push`, { repo: repo || undefined });
    await diff.mutate();
  }, `Pushed ${session.branch}${repo ? ` in ${repo}` : ""}`);

  if (!diff.data) return diff.error ? <ErrorBox error={diff.error} retry={() => diff.mutate()} /> : <Loading />;
  const d = diff.data;
  return (
    <>
      <div className="card">
        <header>
          <h2>
            {repo ? <span className="mono">{repo}/ </span> : null}Changes <span className="plus">+{d.additions}</span> <span className="minus">−{d.deletions}</span>{" "}
            <span className="muted small">
              in {d.files.length} file{d.files.length === 1 ? "" : "s"}
            </span>
          </h2>
          <div className="row">
            <button className="btn sm" onClick={() => diff.mutate()}>
              Refresh
            </button>
            <button className="btn sm primary" disabled={!d.uncommitted} onClick={() => setCommitting(true)} title={d.uncommitted ? "" : "Nothing to commit"}>
              Commit{d.uncommitted ? ` (${d.uncommitted})` : ""}
            </button>
            <button className="btn sm" disabled={pushing || !d.remote} title={d.remote ? `Push to ${d.remote}` : "This repository has no origin remote"} onClick={() => push()}>
              {pushing ? "Pushing…" : "Push"}
            </button>
            <button
              className="btn sm"
              disabled={!system.data?.gh.installed || !d.remote}
              title={!d.remote ? "This repository has no origin remote" : system.data?.gh.installed ? "Push first, then open a PR with gh" : "Install the GitHub CLI (gh) on the server"}
              onClick={() => setPrOpen(true)}
            >
              Open PR
            </button>
          </div>
        </header>
        <div className="muted small">
          Against {d.base ? <code>{d.base.slice(0, 8)}</code> : "HEAD"} (where <code>{session.branch}</code> branched off), including uncommitted work.
          {d.commits.length > 0 && ` ${d.commits.length} commit${d.commits.length === 1 ? "" : "s"} on the branch.`}
        </div>
        <DiffFiles diff={d} onPick={() => {}} />
        {d.commits.length > 0 && (
          <details>
            <summary className="small muted">Commits</summary>
            <div className="stack tight" style={{ marginTop: 6 }}>
              {d.commits.map((c) => (
                <div key={c.sha} className="small">
                  <code>{c.sha}</code> {c.subject}
                </div>
              ))}
            </div>
          </details>
        )}
      </div>
      {d.truncated && <div className="callout warn">The patch is larger than 1 MB and was cut off.</div>}
      <Patch patch={d.patch} />
      {committing && (
        <CommitDialog
          session={session}
          repo={repo}
          defaultMessage={lastSummary?.split("\n")[0]?.slice(0, 72) || `Work from session ${session.name}`}
          onClose={() => setCommitting(false)}
          onDone={(sha) => {
            toast(`Committed ${sha}`);
            void diff.mutate();
            refresh("/api/activity");
          }}
        />
      )}
      {prOpen && <PrDialog session={session} repo={repo} defaultBody={lastSummary} onClose={() => setPrOpen(false)} />}
    </>
  );
}

function CommitDialog({
  session,
  repo,
  defaultMessage,
  onClose,
  onDone,
}: {
  session: Session;
  repo: string;
  defaultMessage: string;
  onClose: () => void;
  onDone: (sha: string) => void;
}) {
  const [message, setMessage] = useState(defaultMessage);
  const [commit, busy] = useAction(async () => {
    const r = await post<{ sha: string }>(`/api/sessions/${session.id}/commit`, { message, repo: repo || undefined });
    onDone(r.sha);
    onClose();
  });
  return (
    <Modal
      title={repo ? `Commit changes in ${repo}/` : "Commit all changes"}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" disabled={busy || !message.trim()} onClick={() => commit()}>
            Commit
          </button>
        </>
      }
    >
      <div className="muted small">
        Runs <code>git add -A && git commit</code> in {repo ? <code>{repo}/</code> : "the worktree"} on <code>{session.branch}</code>.
        {session.state === "working" && " Claude is still working; you may want to wait for the turn to finish."}
      </div>
      <Field label="Message">
        <textarea autoFocus rows={4} value={message} onChange={(e) => setMessage(e.target.value)} />
      </Field>
    </Modal>
  );
}

function PrDialog({ session, repo, defaultBody, onClose }: { session: Session; repo: string; defaultBody?: string; onClose: () => void }) {
  const [title, setTitle] = useState(session.name.replace(/-/g, " ").replace(/^\w/, (c) => c.toUpperCase()));
  const [body, setBody] = useState(defaultBody ?? "");
  const [base, setBase] = useState("");
  const [url, setUrl] = useState<string | null>(null);
  const [open, busy] = useAction(async () => {
    const r = await post<{ url: string }>(`/api/sessions/${session.id}/pr`, { repo: repo || undefined, title, body, base: base.trim() || undefined });
    setUrl(r.url);
  });
  return (
    <Modal
      title="Open a pull request"
      onClose={onClose}
      wide
      footer={
        url ? (
          <a className="btn primary" href={url} target="_blank" rel="noreferrer">
            Open PR ↗
          </a>
        ) : (
          <>
            <button className="btn" onClick={onClose}>
              Cancel
            </button>
            <button className="btn primary" disabled={busy || !title.trim()} onClick={() => open()}>
              {busy ? "Creating…" : "Create pull request"}
            </button>
          </>
        )
      }
    >
      {url ? (
        <div className="callout ok">
          Pull request created: <a href={url} target="_blank" rel="noreferrer">{url}</a>
        </div>
      ) : (
        <>
          <div className="muted small">
            Uses <code>gh pr create --head {session.branch}</code>
            {repo ? (
              <>
                {" "}
                in <code>{repo}/</code>
              </>
            ) : null}
            . Push the branch first.
          </div>
          <Field label="Title">
            <input value={title} onChange={(e) => setTitle(e.target.value)} />
          </Field>
          <Field label="Base branch" hint="Default: the repository's default branch">
            <input className="mono" value={base} placeholder="main" onChange={(e) => setBase(e.target.value)} />
          </Field>
          <Field label="Description">
            <textarea rows={8} value={body} onChange={(e) => setBody(e.target.value)} />
          </Field>
        </>
      )}
    </Modal>
  );
}
